// src/features/auth/context/AuthProvider.jsx
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import * as authService from "../msal/msalAdapter";
import { setAuthTokenGetter, setActorGetter } from "../../../api/axios";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [booting, setBooting] = useState(true);

  // Boot MSAL e registra token getter (uma vez)
  useEffect(() => {
    let alive = true;

    (async () => {
      const { user } = await authService.init(); // tenta restaurar sessão
      if (!alive) return;
      setUser(user || null);
      setBooting(false);
    })();

    // Axios → Authorization: Bearer <access_token>
    setAuthTokenGetter(async () => {
      try {
        const t = await authService.getAccessToken();
        return t || null;
      } catch {
        return null;
      }
    });

    // Refresh leve quando a aba volta ao foco (mantém sessão viva)
    const onVis = async () => {
      if (document.visibilityState === "visible") {
        await authService.getAccessToken().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVis);
      setAuthTokenGetter(null);
      setActorGetter(null);
    };
  }, []);

  // Sempre que o usuário mudar, enviaremos X-Actor com o nome “bonito”
  useEffect(() => {
    // Axios → X-Actor: <nome para logs/auditoria>
    setActorGetter(() => user?.name || user?.preferred_username || null);
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      booting,
      isAuthenticated: !!user,
      login: authService.login,
      logout: authService.logout,
      getAccessToken: authService.getAccessToken,
    }),
    [user, booting]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
